import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Globe, MessageCircle, AlertCircle, HelpCircle } from "lucide-react";

const SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];

export default function CompanyProfile() {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({
    name: "", description: "", industry: "", size: "", founded_year: "",
    website: "", city: "", address: "", mobile: "", email: "",
    logo: "", social_linkedin: "", google_profile: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/companies/my").then(r => {
      if (r.data) {
        setCompany(r.data);
        setForm({ ...form, ...r.data });
      }
    });
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadLogo = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", r.data.url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, founded_year: form.founded_year ? parseInt(form.founded_year) : null };
      if (company) {
        await api.put("/companies/my", payload);
        toast.success("Company updated");
      } else {
        const r = await api.post("/companies", payload);
        setCompany(r.data);
        toast.success(`Company created (${r.data.company_id}) — awaiting admin approval`);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const whatsappMsg = `Hi, I want to create a website or refurbish my website. My company name is ${form.name || "[your company]"}.`;
  const whatsappLink = `https://wa.me/917411298777?text=${encodeURIComponent(whatsappMsg)}`;
  const noWebsite = !form.website || form.website.trim().length === 0;

  return (
    <div data-testid="company-profile-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-[#1D6FF2]" /> Company Profile</h2>
          <p className="text-slate-500 text-sm">Create your company to start hiring on Job Dew.</p>
        </div>
        {company && (
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="font-mono text-[#1D6FF2] border-[#1D6FF2]">{company.company_id}</Badge>
            <Badge className={
              company.status === "approved" ? "badge-approved" :
              company.status === "rejected" ? "badge-rejected" : "badge-pending"
            } data-testid="company-status">{company.status}</Badge>
          </div>
        )}
      </div>

      {company?.status === "pending" && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex gap-3" data-testid="company-pending-banner">
          <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
          <div className="text-sm text-yellow-900">
            <b>Awaiting admin approval.</b> You can edit details below. Once approved, you can post jobs.
          </div>
        </div>
      )}

      {company?.status === "rejected" && company?.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-800" data-testid="company-rejected-banner">
          <b>Rejected:</b> {company.rejection_reason}. Update your details and resave to re-submit.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Company Name *</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="company-name-input" /></div>
          <div><Label>Industry</Label><Input value={form.industry} onChange={e => set("industry", e.target.value)} placeholder="e.g. IT, Retail" /></div>
          <div>
            <Label>Company Size</Label>
            <select className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" value={form.size} onChange={e => set("size", e.target.value)}>
              <option value="">Select</option>
              {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Founded Year</Label><Input type="number" value={form.founded_year || ""} onChange={e => set("founded_year", e.target.value)} placeholder="2020" /></div>
          <div><Label>City</Label><Input value={form.city} onChange={e => set("city", e.target.value)} /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => set("mobile", e.target.value)} /></div>
          <div><Label>Company Email</Label><Input value={form.email} onChange={e => set("email", e.target.value)} /></div>
          <div><Label>Website</Label><Input value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://yourcompany.com" data-testid="company-website-input" /></div>
          <div className="md:col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>About / Description</Label><Textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)} /></div>
          <div><Label>LinkedIn URL</Label><Input value={form.social_linkedin} onChange={e => set("social_linkedin", e.target.value)} /></div>

          {/* Google Profile URL + hover-info button */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Label className="mb-0">Google Profile URL</Label>
              <div className="relative group">
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label="How to get your Google profile URL"
                  className="w-5 h-5 rounded-full border border-slate-300 text-slate-400 flex items-center justify-center hover:bg-[#1D6FF2] hover:text-white hover:border-[#1D6FF2] transition"
                  data-testid="google-profile-help-btn"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
                {/* Tooltip */}
                <div
                  role="tooltip"
                  className="pointer-events-none absolute left-0 bottom-full mb-2 w-72 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs leading-relaxed shadow-lg opacity-0 invisible -translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-30"
                >
                  On Google Maps, type the business name into the search bar, select it, and click or tap the <b>Share</b> icon to copy its direct profile URL.
                  <span className="absolute left-3 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900" />
                </div>
              </div>
            </div>
            <Input
              value={form.google_profile}
              onChange={e => set("google_profile", e.target.value)}
              placeholder="https://maps.app.goo.gl/..."
              data-testid="company-google-profile-input"
            />
          </div>

          <div className="md:col-span-2">
            <Label>Logo</Label>
            <Input type="file" accept="image/*" onChange={uploadLogo} data-testid="company-logo-upload" />
            {form.logo && <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo}`} alt="logo" className="w-20 h-20 mt-2 rounded-lg object-cover border" />}
          </div>
        </div>

        {noWebsite && (
          <div className="bg-gradient-to-r from-[#E8F0FE] to-white border-2 border-[#1D6FF2]/30 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="no-website-banner">
            <div className="flex items-center gap-3">
              <Globe className="w-8 h-8 text-[#1D6FF2]" />
              <div>
                <div className="font-semibold text-slate-900">Don't have a website yet?</div>
                <div className="text-sm text-slate-600">Get one built or refurbished by our X7 IT Technologies.</div>
              </div>
            </div>
            <a href={whatsappLink} target="_blank" rel="noreferrer">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="whatsapp-website-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
              </Button>
            </a>
          </div>
        )}

        <Button onClick={submit} disabled={busy || !form.name} className="bg-[#1D6FF2] hover:bg-[#1659C7] text-white" data-testid="save-company-btn">
          {busy ? "Saving…" : (company ? "Update Company" : "Create Company")}
        </Button>
      </div>
    </div>
  );
}